// Global Types
import type {
  $Location,
} from '@vroskus/library-navigation';

// Global Enums
import {
  DeploymentEnvironment,
} from 'common.enums';

// Helpers
import React from 'react';
import {
  Provider,
} from 'react-redux';
import {
  BrowserRouter,
} from 'react-router-dom';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  initReactI18next,
} from 'react-i18next';
import {
  getLanguageResource,
  i18next,
  language,
  languageResourceType,
} from 'common.language';
import configureStore from './redux';
import config, {
  packageVersion,
} from './config';

// Routes
import Routes from './routes';

// Components
import MainLayout from './components/layout/Main';

// Vendors
import './theme/Vendor';

// Styles
import './styles.css';

// Services
import Services from './services';

// Types
declare global {
  interface Window {
    devToolsExtension: () => () => unknown;
    Services: typeof Services;
  }
}

i18next.use(initReactI18next).init({
  debug: false,
  fallbackLng: language.en,
  interpolation: {
    escapeValue: false,
  },
  resources: getLanguageResource(languageResourceType.admin),
});

ErrorLog.init(
  {
    denyUrls: [/mixpanel\.com/i],
    dsn: config.SENTRY_DSN,
    enableTracing: false,
    environment: config.DEPLOYMENT_ENV,
    release: packageVersion,
  },
  [DeploymentEnvironment.production],
  {
    [DeploymentEnvironment.development]: 'error',
    [DeploymentEnvironment.test]: 'error',
  },
);

const runBeforeAppStart = (): void => {
  Services.Api.setRequestContextListener({
    listener: (context) => ErrorLog.context(
      'Api request context',
      context,
    ),
  });

  Services.Api.setResponseContextListener({
    listener: ({
      Duration,
      Method,
      Route,
      Status,
    }) => {
      Services.Mixpanel.trackEvent(
        Services.Mixpanel.event.request,
        {
          Duration,
          Method,
          Route,
          Status,
        },
      );
    },
  });

  Services.Crisp.init();

  Services.Navigation.addListener((location: $Location) => {
    Services.Mixpanel.trackEvent(
      Services.Mixpanel.event.pageVisit,
      location,
    );
  });
};

const {
  Navigation: {
    render: Navigation,
  },
} = Services;

const App = function () {
  const store = configureStore(Services);

  runBeforeAppStart();

  return (
    <Provider store={store}>
      <BrowserRouter>
        <MainLayout>
          <Routes />
        </MainLayout>
        <Navigation />
      </BrowserRouter>
    </Provider>
  );
};

export default App;
